import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, Loader2, ShieldCheck } from "lucide-react";
import { MaskedLines, EASE } from "@/components/motion";
import Field from "@/components/alta/Field";
import LegalModal from "@/components/alta/LegalModal";
import { TERMS_VERSION, TERMS_DOC, PRIVACY_DOC, INDUSTRY_SUGGESTIONS } from "@/data/condiciones";
import { validateStep, stepOfField } from "@/utils/validation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { num: "01", label: "EMPRESA" },
  { num: "02", label: "CONTACTO" },
  { num: "03", label: "CONDICIONES" },
];

const INITIAL = {
  legal_name: "",
  tax_id: "",
  website: "",
  industry: "",
  employee_count: "",
  first_name: "",
  last_name: "",
  position: "",
  email: "",
  phone: "",
  terms_accepted: false,
  marketing_consent: false,
};

function StepProgress({ step }) {
  return (
    <div className="flex items-center gap-0 mb-16" data-testid="alta-progress">
      {STEPS.map((s, i) => (
        <div key={s.num} className="flex items-center flex-1 last:flex-none">
          <div className="flex items-center gap-3">
            <span
              className={`font-mono text-[11px] tracking-[0.2em] transition-colors duration-500 ${
                i < step ? "text-silver" : i === step ? "text-glow" : "text-steel/60"
              }`}
            >
              {s.num}
            </span>
            <span
              className={`font-mono text-[11px] tracking-[0.25em] transition-colors duration-500 hidden sm:block ${
                i <= step ? "text-offwhite" : "text-steel/60"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <span className="relative flex-1 h-px bg-silver/10 mx-4 min-w-6">
              <motion.span
                className="absolute inset-y-0 left-0 bg-glow/70"
                initial={false}
                animate={{ width: i < step ? "100%" : "0%" }}
                transition={{ duration: 0.6, ease: EASE }}
              />
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function CheckRow({ checked, onChange, label, error, testid }) {
  return (
    <div>
      <label className="flex items-start gap-4 cursor-pointer group select-none">
        <span className="relative mt-0.5 shrink-0">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className="peer sr-only"
            data-testid={testid}
            aria-invalid={!!error}
          />
          <span
            className={`flex h-5 w-5 items-center justify-center border transition-colors duration-300 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-glow/70 ${
              error ? "border-red-400/70" : "border-silver/30 group-hover:border-silver/60"
            } ${checked ? "bg-silver border-silver" : "bg-transparent"}`}
          >
            {checked && <Check className="h-3.5 w-3.5 text-night" strokeWidth={3} />}
          </span>
        </span>
        <span className="text-sm text-mist leading-relaxed group-hover:text-silver transition-colors duration-300">
          {label}
        </span>
      </label>
      {error && (
        <p role="alert" data-testid={`${testid}-error`} className="mt-2 text-xs text-red-300/90">
          {error}
        </p>
      )}
    </div>
  );
}

function Confirmation({ result }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: EASE }}
      className="max-w-2xl"
      data-testid="alta-confirmation"
    >
      <div className="flex items-center gap-3 mb-10">
        <ShieldCheck className="h-5 w-5 text-glow" strokeWidth={1.5} />
        <span className="font-mono text-[11px] tracking-[0.4em] text-glow/80">REGISTRO COMPLETADO</span>
      </div>
      <h2 className="font-display font-800 uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl text-offwhite mb-8">
        Datos
        <br />
        <span className="font-cormorant normal-case font-medium text-metal">registrados.</span>
      </h2>
      <p className="text-mist leading-relaxed mb-10 max-w-lg">
        Tu información se ha registrado correctamente. Nos pondremos en contacto contigo para activar tu
        servicio. No se ha realizado ningún cargo.
      </p>
      <div className="border border-silver/15 bg-panel/40 p-7 mb-12">
        <span className="font-mono text-[10px] tracking-[0.35em] text-steel block mb-3">
          IDENTIFICADOR DE TU REGISTRO
        </span>
        <span className="font-mono text-2xl md:text-3xl text-metal" data-testid="alta-registration-id">
          {result.registration_id}
        </span>
        <div className="mt-5 pt-5 border-t border-silver/10 flex flex-wrap gap-x-10 gap-y-2 font-mono text-[10px] tracking-[0.2em] text-steel">
          <span>
            ESTADO — <span className="text-glow/90">REGISTRADO</span>
          </span>
          <span>CONDICIONES ACEPTADAS — v{result.terms_version}</span>
        </div>
      </div>
      <Link
        to="/"
        data-testid="alta-confirmation-home"
        className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] border border-silver/30 text-silver hover:border-silver hover:text-offwhite px-8 py-4 transition-colors duration-300"
      >
        VOLVER AL INICIO
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </motion.div>
  );
}

export default function Alta() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [touchedSubmit, setTouchedSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [result, setResult] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const set = (name) => (e) => {
    const value = e.target ? e.target.value : e;
    setData((d) => ({ ...d, [name]: value }));
    if (touchedSubmit) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const blur = (name) => () => {
    const errs = validateStep(step, { ...data });
    if (errs[name]) setErrors((prev) => ({ ...prev, [name]: errs[name] }));
  };

  const next = () => {
    const errs = validateStep(step, data);
    setErrors(errs);
    setTouchedSubmit(true);
    if (Object.keys(errs).length === 0) {
      setStep((s) => Math.min(s + 1, 2));
      setTouchedSubmit(false);
      window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo(0, 0);
    }
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    const errs = { ...validateStep(0, data), ...validateStep(1, data), ...validateStep(2, data) };
    setErrors(errs);
    setTouchedSubmit(true);
    if (Object.keys(errs).length > 0) {
      setStep(stepOfField(Object.keys(errs)[0]));
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const res = await fetch(`${API}/registrations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company: {
            legal_name: data.legal_name,
            tax_id: data.tax_id,
            website: data.website || null,
            industry: data.industry,
            employee_count: data.employee_count === "" ? null : Number(data.employee_count),
          },
          contact: {
            first_name: data.first_name,
            last_name: data.last_name,
            position: data.position,
            email: data.email,
            phone: data.phone || null,
          },
          terms_accepted: data.terms_accepted,
          marketing_consent: data.marketing_consent,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 422 && body.detail && typeof body.detail === "object") {
        const be = {};
        for (const [k, v] of Object.entries(body.detail)) be[k] = typeof v === "string" ? v : "Valor no válido";
        setErrors(be);
        setStep(stepOfField(Object.keys(be)[0]));
        return;
      }
      if (!res.ok) throw new Error("server");
      setResult(body);
      window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
    } catch {
      setServerError("No hemos podido registrar tus datos en este momento. Inténtalo de nuevo en unos minutos.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="page-alta" className="min-h-screen blueprint-grid">
      <div className="mx-auto w-full max-w-[1100px] px-6 lg:px-12 pt-40 lg:pt-48 pb-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-mono text-[11px] tracking-[0.4em] text-glow/80 mb-8"
        >
          ALTA — {STEPS[step].num} / 03
        </motion.p>

        {result ? (
          <Confirmation result={result} />
        ) : (
          <>
            <h1 className="font-display font-800 uppercase leading-[0.95] tracking-tight text-[11vw] sm:text-7xl lg:text-8xl text-offwhite mb-8">
              <MaskedLines
                mode="load"
                delay={0.3}
                lines={[
                  <span key="a">Empieza</span>,
                  <span key="b">
                    con <span className="font-cormorant normal-case font-medium text-metal">Aprea.</span>
                  </span>,
                ]}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75 }}
              className="max-w-xl text-mist text-base md:text-lg leading-relaxed mb-6"
            >
              Cuéntanos quién eres y dónde operas. Registraremos tu alta y nos pondremos en contacto contigo
              para activar el servicio.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1 }}
              className="max-w-xl text-xs text-steel leading-relaxed mb-16"
              data-testid="alta-price-note"
            >
              La contratación del servicio ofrecido por Aprea Gestora tiene un coste de 12,99 € mensuales, con
              cargo recurrente mediante tarjeta bancaria a través de una plataforma de pago online segura. La
              contratación implica la aceptación de estas condiciones y la autorización del correspondiente
              cargo mensual mientras el servicio permanezca activo.
            </motion.p>

            <StepProgress step={step} />

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.5, ease: EASE }}
              >
                {step === 0 && (
                  <section data-testid="alta-step-empresa">
                    <h2 className="font-oswald font-600 uppercase text-2xl md:text-3xl tracking-wide text-offwhite mb-10">
                      Datos de la empresa
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                      <Field label="RAZÓN SOCIAL / NOMBRE DE LA EMPRESA" name="legal_name" value={data.legal_name} onChange={set("legal_name")} onBlur={blur("legal_name")} error={errors.legal_name} autoComplete="organization" testid="alta-field-legal-name" />
                      <Field label="CIF / NIF" name="tax_id" value={data.tax_id} onChange={set("tax_id")} onBlur={blur("tax_id")} error={errors.tax_id} placeholder="B12345678" testid="alta-field-tax-id" />
                      <Field label="PÁGINA WEB" name="website" type="url" value={data.website} onChange={set("website")} onBlur={blur("website")} error={errors.website} optional placeholder="https://tuempresa.com" inputMode="url" autoComplete="url" testid="alta-field-website" />
                      <div>
                        <Field label="SECTOR / ACTIVIDAD" name="industry" value={data.industry} onChange={set("industry")} onBlur={blur("industry")} error={errors.industry} placeholder="Restaurante, peluquería, tienda…" list="industry-options" testid="alta-field-industry" />
                        <datalist id="industry-options">
                          {INDUSTRY_SUGGESTIONS.map((s) => (
                            <option key={s} value={s} />
                          ))}
                        </datalist>
                      </div>
                      <Field label="Nº APROXIMADO DE EMPLEADOS" name="employee_count" type="number" value={data.employee_count} onChange={set("employee_count")} onBlur={blur("employee_count")} error={errors.employee_count} optional inputMode="numeric" testid="alta-field-employees" />
                    </div>
                  </section>
                )}

                {step === 1 && (
                  <section data-testid="alta-step-contacto">
                    <h2 className="font-oswald font-600 uppercase text-2xl md:text-3xl tracking-wide text-offwhite mb-10">
                      Persona de contacto
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                      <Field label="NOMBRE" name="first_name" value={data.first_name} onChange={set("first_name")} onBlur={blur("first_name")} error={errors.first_name} autoComplete="given-name" testid="alta-field-first-name" />
                      <Field label="APELLIDOS" name="last_name" value={data.last_name} onChange={set("last_name")} onBlur={blur("last_name")} error={errors.last_name} autoComplete="family-name" testid="alta-field-last-name" />
                      <Field label="CARGO / PUESTO EN LA EMPRESA" name="position" value={data.position} onChange={set("position")} onBlur={blur("position")} error={errors.position} placeholder="Propietaria, gerente…" autoComplete="organization-title" testid="alta-field-position" />
                      <Field label="TELÉFONO" name="phone" type="tel" value={data.phone} onChange={set("phone")} onBlur={blur("phone")} error={errors.phone} optional placeholder="612 345 678" inputMode="tel" autoComplete="tel" testid="alta-field-phone" />
                      <div className="md:col-span-2">
                        <Field label="EMAIL PROFESIONAL" name="email" type="email" value={data.email} onChange={set("email")} onBlur={blur("email")} error={errors.email} placeholder="nombre@tuempresa.com" inputMode="email" autoComplete="email" testid="alta-field-email" />
                      </div>
                    </div>
                  </section>
                )}

                {step === 2 && (
                  <section data-testid="alta-step-condiciones">
                    <h2 className="font-oswald font-600 uppercase text-2xl md:text-3xl tracking-wide text-offwhite mb-10">
                      Condiciones del servicio
                    </h2>
                    <div className="border border-silver/15 bg-panel/40 p-7 md:p-9 mb-10">
                      <div className="flex flex-wrap items-center justify-between gap-6">
                        <div>
                          <span className="font-mono text-[10px] tracking-[0.3em] text-steel block mb-2">
                            DOCUMENTO — VERSIÓN {TERMS_VERSION}
                          </span>
                          <span className="font-oswald uppercase text-lg tracking-wide text-offwhite">
                            Condiciones del Servicio de Aprea Gestora
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setTermsOpen(true)}
                          data-testid="alta-terms-open"
                          className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-glow hover:text-offwhite border border-glow/40 hover:border-silver px-6 py-3 transition-colors duration-300"
                        >
                          LEER LAS CONDICIONES DEL SERVICIO
                          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-6 mb-10">
                      <CheckRow
                        checked={data.terms_accepted}
                        onChange={(v) => set("terms_accepted")(v)}
                        error={errors.terms_accepted}
                        testid="alta-terms-checkbox"
                        label={
                          <>
                            He leído y acepto las{" "}
                            <button type="button" onClick={() => setTermsOpen(true)} className="text-silver underline underline-offset-4 decoration-silver/40 hover:decoration-silver transition-[text-decoration-color] duration-300">
                              Condiciones del Servicio
                            </button>
                            .
                          </>
                        }
                      />
                      <CheckRow
                        checked={data.marketing_consent}
                        onChange={(v) => set("marketing_consent")(v)}
                        testid="alta-marketing-checkbox"
                        label="Quiero recibir comunicaciones comerciales. (Opcional)"
                      />
                    </div>

                    <p className="text-xs text-steel leading-relaxed max-w-xl border-l border-silver/15 pl-5">
                      Tus datos se utilizarán exclusivamente para gestionar tu alta y preparar la contratación
                      del servicio. Consulta nuestra{" "}
                      <button
                        type="button"
                        onClick={() => setPrivacyOpen(true)}
                        data-testid="alta-privacy-open"
                        className="text-mist underline underline-offset-4 decoration-steel/50 hover:text-silver transition-colors duration-300"
                      >
                        POLÍTICA DE PRIVACIDAD
                      </button>
                      .
                    </p>
                  </section>
                )}
              </motion.div>
            </AnimatePresence>

            {serverError && (
              <p role="alert" data-testid="alta-server-error" className="mt-8 text-sm text-red-300/90 border border-red-400/30 bg-red-400/5 px-6 py-4">
                {serverError}
              </p>
            )}

            <div className="mt-14 flex flex-wrap items-center gap-5">
              {step > 0 && (
                <button
                  type="button"
                  onClick={back}
                  data-testid="alta-back"
                  className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] border border-silver/30 text-mist hover:text-offwhite hover:border-silver px-8 py-4 transition-colors duration-300"
                >
                  <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
                  ATRÁS
                </button>
              )}
              {step < 2 ? (
                <motion.span whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} className="inline-block">
                  <button
                    type="button"
                    onClick={next}
                    data-testid="alta-next"
                    className="group inline-flex items-center gap-3 bg-silver text-night font-mono text-xs tracking-[0.25em] px-8 py-4 hover:bg-offwhite transition-colors duration-300"
                  >
                    CONTINUAR
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </button>
                </motion.span>
              ) : (
                <motion.span whileHover={submitting ? {} : { y: -2 }} whileTap={submitting ? {} : { scale: 0.97 }} className="inline-block">
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    data-testid="alta-submit"
                    className={`inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] px-10 py-4 transition-colors duration-300 ${
                      submitting
                        ? "bg-steel/40 text-mist cursor-not-allowed"
                        : "bg-silver text-night hover:bg-offwhite"
                    }`}
                  >
                    {submitting ? "REGISTRANDO DATOS…" : "DARME DE ALTA"}
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  </button>
                </motion.span>
              )}
            </div>
            {step === 2 && !submitting && (
              <p className="mt-5 font-mono text-[10px] tracking-[0.2em] text-steel">
                NO SE REALIZA NINGÚN PAGO EN ESTE PASO.
              </p>
            )}
          </>
        )}
      </div>

      <LegalModal open={termsOpen} onClose={() => setTermsOpen(false)} title="Condiciones del Servicio" version={TERMS_VERSION} doc={TERMS_DOC} testid="alta-terms-modal" />
      <LegalModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" version={null} doc={PRIVACY_DOC} testid="alta-privacy-modal" />
    </main>
  );
}
